import React, { useEffect, useState } from "react";
import api from "../lib/api";
import CommunityPostCard from "../components/CommunityPostCard";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Send } from "lucide-react";

export default function Community() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [posting, setPosting] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/community/posts?limit=50");
      setPosts(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [user?.id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) return;
    setPosting(true);
    try {
      await api.post("/community/posts", { title, content });
      setTitle(""); setContent("");
      toast.success("Posted to the feed");
      load();
    } catch (err) {
      toast.error("Could not publish post");
    } finally { setPosting(false); }
  };

  return (
    <div className="pt-24 pb-20">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10 bg-black">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: "radial-gradient(circle at 20% 50%, #facc15 0%, transparent 40%), radial-gradient(circle at 80% 30%, #3b82f6 0%, transparent 40%)",
        }} />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-20">
          <span className="font-mono text-xs text-yellow-400 uppercase tracking-[0.3em]">/community</span>
          <h1 className="font-display font-bold text-5xl md:text-6xl text-white tracking-tighter mt-2">
            The Feed
          </h1>
          <p className="text-zinc-300 mt-2">High-signal posts from the MediaCrypto community.</p>
        </div>
      </section>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 space-y-6">
          {user ? (
            <form
              onSubmit={submit}
              className="bg-[#0a0a0b] border border-white/10 p-6"
              data-testid="community-composer"
            >
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Post title..."
                data-testid="community-post-title-input"
                className="w-full bg-transparent border-b border-white/10 px-0 py-2 text-lg font-display font-bold focus:outline-none focus:border-yellow-400 text-white placeholder:text-zinc-600"
              />
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Share your alpha, charts, or hot takes..."
                rows={4}
                data-testid="community-post-content-input"
                className="w-full mt-4 bg-transparent text-zinc-200 placeholder:text-zinc-500 focus:outline-none resize-none"
              />
              <div className="flex justify-between items-center mt-3 pt-3 border-t border-white/10">
                <span className="text-xs font-mono text-zinc-500">Posting as <span className="text-zinc-300">{user.name}</span></span>
                <button
                  type="submit"
                  disabled={posting}
                  data-testid="community-post-submit-button"
                  className="bg-yellow-400 text-black font-bold uppercase tracking-wide px-5 py-2 hover:bg-yellow-500 transition disabled:opacity-50 flex items-center gap-2"
                >
                  <Send className="w-4 h-4" /> Publish
                </button>
              </div>
            </form>
          ) : (
            <div className="bg-[#0a0a0b] border border-white/10 p-8 text-center">
              <p className="text-zinc-300 mb-4 text-lg">Sign in to publish posts and join the conversation.</p>
              <div className="flex justify-center gap-3">
                <Link to="/login" className="border border-white/20 hover:border-yellow-400 hover:text-yellow-400 px-6 py-2 text-sm uppercase tracking-wide transition text-white">Sign in</Link>
                <Link to="/register" className="bg-yellow-400 text-black font-bold uppercase tracking-wide px-6 py-2 hover:bg-yellow-500 transition">Join free</Link>
              </div>
            </div>
          )}

          <div className="space-y-4" data-testid="community-feed">
            {loading ? (
              <div className="text-zinc-500 font-mono text-sm">Loading feed...</div>
            ) : posts.length === 0 ? (
              <div className="text-zinc-500 p-8 border border-white/10 bg-[#0a0a0b] text-center">
                <p className="text-lg font-display font-bold text-white mb-2">No posts yet.</p>
                <p className="text-sm">Be the first to start the conversation.</p>
              </div>
            ) : (
              [...posts].sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0)).map((p) => <CommunityPostCard key={p.id} post={p} onChanged={load} />)
            )}
          </div>
        </div>

        <aside className="lg:col-span-4 space-y-6">
          <div className="bg-[#0a0a0b] border border-white/10 p-6">
            <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400 mb-4">Community Guidelines</h3>
            <ul className="space-y-3 text-sm text-zinc-300">
              <li className="flex gap-2"><span className="text-yellow-400">—</span> High-signal posts only.</li>
              <li className="flex gap-2"><span className="text-yellow-400">—</span> No financial advice.</li>
              <li className="flex gap-2"><span className="text-yellow-400">—</span> Cite sources for charts and data.</li>
              <li className="flex gap-2"><span className="text-yellow-400">—</span> Disagree with ideas, not people.</li>
            </ul>
          </div>
          <div className="bg-[#0a0a0b] border border-white/10 p-6">
            <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400 mb-4">Trending Topics</h3>
            <div className="flex flex-wrap gap-2">
              {["#BTC", "#ETH", "#Solana", "#Pectra", "#ETF", "#DeFi", "#Macro"].map((t) => (
                <span key={t} className="px-3 py-1 border border-white/10 text-xs font-mono text-zinc-300 hover:text-yellow-400 hover:border-yellow-400/40 cursor-pointer transition">{t}</span>
              ))}
            </div>
          </div>
          {!user && (
            <div className="bg-yellow-400/5 border border-yellow-400/20 p-6">
              <p className="text-sm text-zinc-300 mb-3">Join MediaCrypto to post, like, and comment.</p>
              <Link to="/register" className="block text-center bg-yellow-400 text-black font-bold uppercase tracking-wide px-4 py-2 hover:bg-yellow-500 transition text-sm">
                Create Account
              </Link>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}